import { OtsDateTime } from '../../components/OtsDateTime';
import { useState } from 'react';
import type { RrcCompanyProfile } from '@ots-frontend/ots-data';
import { OtsMasterDetail } from '../../components/OtsMasterDetail';
import { OtsActionButton } from '../../components/OtsButton';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { OtsDataTableEmpty } from '../../components/OtsDataTableCells';
import { ComplianceStatusBadge } from '../ComplianceStatusBadge';
import { RrcCompanyProfileDetails } from './RrcCompanyProfileDetails';

export type RrcCompanyProfileViewProps = {
  profile: RrcCompanyProfile;
  versions?: RrcCompanyProfile[];
  loadingVersions?: boolean;
  versionsError?: string;
  onRetry?: () => void;
  onEdit?: (profile: RrcCompanyProfile) => void;
  onCopy?: (profile: RrcCompanyProfile) => void;
  onDelete?: (profile: RrcCompanyProfile) => void;
  canEdit?: (profile: RrcCompanyProfile) => boolean;
  canDelete?: (profile: RrcCompanyProfile) => boolean;
  readOnly?: boolean;
  compact?: boolean;
};
export const RrcCompanyProfileView = ({
  profile,
  versions = [],
  loadingVersions,
  versionsError,
  onRetry,
  onEdit,
  onCopy,
  onDelete,
  canEdit = () => true,
  canDelete = () => true,
  readOnly,
  compact,
}: RrcCompanyProfileViewProps) => {
  const [selectedId, select] = useState(profile.id);
  const history = [...versions].sort(
    (left, right) => right.version - left.version
  );
  const selected =
    history.find((version) => version.id === selectedId) ?? profile;
  const actions = (
    <div className="flex flex-wrap justify-end gap-2">
      {!readOnly && onCopy && (
        <OtsActionButton size="compact" onClick={() => onCopy(selected)}>
          Copy as new version
        </OtsActionButton>
      )}
      {!readOnly && onEdit && canEdit(selected) && (
        <OtsActionButton
          size="compact"
          variant="primary"
          onClick={() => onEdit(selected)}
        >
          Edit profile
        </OtsActionButton>
      )}
      {!readOnly && onDelete && canDelete(selected) && (
        <OtsActionButton
          size="compact"
          variant="destructive"
          onClick={() => onDelete(selected)}
        >
          Delete profile
        </OtsActionButton>
      )}
    </div>
  );
  return (
    <div className="@container space-y-4">
      {readOnly && (
        <OtsInlineAlert
          title="View only"
          size={compact ? 'compact' : 'default'}
        >
          Open Company Profiles in the owning tenant to manage this record.
        </OtsInlineAlert>
      )}
      <OtsMasterDetail
        navigationTitle="Version history"
        navigationLabel="Company profile versions"
        items={history.map((version) => ({
          id: version.id,
          label: `Version ${version.version} · ${version.status ?? 'Unknown'}`,
          ariaLabel: `Company profile version ${version.version}`,
          content: (
            <>
              <p className="mb-1 text-sm font-semibold text-[var(--ots-secondary)]">
                Version {version.version}
              </p>
              <ComplianceStatusBadge status={version.status} compact />
              <p className="mt-2 text-xs text-[var(--ots-text-muted)]">
                <OtsDateTime
                  value={version.effectiveStartDttm}
                  empty="Not set"
                />
              </p>
              <p className="mt-1 text-xs text-[var(--ots-text-muted)]">
                <OtsDateTime
                  value={version.effectiveEndDttm}
                  empty="No end date"
                />
              </p>
            </>
          ),
        }))}
        selectedId={selected.id}
        onSelect={select}
        compact={compact}
        navigationState={
          loadingVersions ? (
            <OtsDataTableEmpty
              loading
              loadingText="Loading company profile versions…"
            >
              {null}
            </OtsDataTableEmpty>
          ) : versionsError ? (
            <OtsInlineAlert
              tone="danger"
              title="History unavailable"
              action={
                onRetry && (
                  <OtsActionButton size="compact" onClick={onRetry}>
                    Try again
                  </OtsActionButton>
                )
              }
            >
              {versionsError}
            </OtsInlineAlert>
          ) : !history.length ? (
            <p className="text-sm text-[var(--ots-text-muted)]">
              No additional versions found.
            </p>
          ) : undefined
        }
      >
        <RrcCompanyProfileDetails
          profile={selected}
          compact={compact}
          actions={actions}
        />
      </OtsMasterDetail>
    </div>
  );
};
